// The call state machine — docs/02-signaling-and-api.md §4, docs/01 §5.
//
// Pure and synchronous: `transition(selfUuid, state, event)` returns the next state plus
// a list of effects for the runtime (send frame, arm timer, open/close the
// peer connection…). All WebRTC/network mechanics live in the session layer;
// everything that decides *what happens next* lives here, so every transition
// — including glare and `answered_elsewhere` — is unit-testable without a
// browser.
//
// Prime directive: Connection > Latency > Quality. Teardown transitions exist
// only where the spec demands them (explicit end, ring timeout, 20 s ICE
// setup failure, 60 s reconnect budget, RTP timeout after a failed restart);
// every other trouble signal degrades or reconnects instead of dropping.

import { TIMEOUTS, type CallDeclineReason, type CallEndReason } from '@merkury/protocol';
import type { CallDirection, CallEndCause, CallPhase } from './types.js';

/**
 * ICE setup deadline: "Connecting --> Idle: 20 s without ICE ⇒ failed"
 * (docs/02 §4 state chart).
 */
export const ICE_CONNECT_TIMEOUT_MS = TIMEOUTS.ICE_CONNECT_MS;

/**
 * Perfect-negotiation politeness and the crossed-call tiebreak use the same
 * UUID string comparison (docs/02 §4): the peer with the HIGHER UUID is the
 * polite one — it yields in glare and cancels its own invite in a crossed
 * call.
 */
export function isPolite(myUuid: string, peerUuid: string): boolean {
  return myUuid > peerUuid;
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export type CallRole = 'caller' | 'callee';

export interface MachineCall {
  callId: string;
  peer: string;
  role: CallRole;
}

export interface MachineState {
  phase: CallPhase;
  call: MachineCall | null;
  /**
   * Crossed-call bookkeeping on the lower-UUID (impolite) side: the incoming
   * invite from the peer we are already dialing. We neither ring nor answer
   * "busy" — the higher-UUID peer will cancel it with `call.end {crossed}`.
   */
  shadowedInviteId: string | null;
  /**
   * Bounded memory of callIds that already reached a terminal state. TERMINAL
   * IS TERMINAL (docs/02 §4): a replayed or delayed `call.invite` for one of
   * these — push + socket + resume-replay can all deliver the same one, and a
   * cancelled/declined call can be re-offered by a lagging relay — must be
   * dropped, never re-rung and never resurrected into UI state.
   */
  endedCallIds: readonly string[];
}

/** How many terminal callIds to remember (a call churns few ids; this is ample). */
const MAX_REMEMBERED_ENDED = 32;

function rememberEnded(prev: readonly string[], callId: string): readonly string[] {
  if (prev.includes(callId)) return prev;
  const next = [...prev, callId];
  return next.length > MAX_REMEMBERED_ENDED ? next.slice(next.length - MAX_REMEMBERED_ENDED) : next;
}

export const IDLE_STATE: MachineState = {
  phase: 'idle',
  call: null,
  shadowedInviteId: null,
  endedCallIds: [],
};

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

export type TimerId = 'ring' | 'connect' | 'reconnect';

export type MachineEvent =
  // user intents
  | { t: 'dial'; callId: string; peer: string }
  | { t: 'accept' }
  | { t: 'decline' }
  | { t: 'hangup' }
  // verified signaling frames (the session verifies signatures first)
  // ringMs bounds the incoming ring window (default RING_MS): a late-delivered
  // invite reconciled from call.sync rings only its remaining server deadline.
  | { t: 'invite'; callId: string; from: string; ringMs?: number }
  | { t: 'remoteRinging'; callId: string }
  | { t: 'remoteAccept'; callId: string }
  | { t: 'remoteDecline'; callId: string; reason: CallDeclineReason }
  | { t: 'remoteEnd'; callId: string; reason: CallEndReason }
  // RTC transport signals
  | { t: 'iceConnected' }
  | { t: 'iceDisconnected' } // `disconnected` or `failed` while a call is up
  | { t: 'iceFailed' } // a restart attempt failed while reconnecting
  | { t: 'networkChange' } // OS-level network change (Wi-Fi ↔ LTE)
  | { t: 'rtpStalled' } // quality monitor: inbound media stalled (soft)
  | { t: 'rtpTimeout' } // 30 s without media + failed ICE restart (hard)
  | { t: 'securityAbort' } // SDP signature verification failed mid-call
  | { t: 'mediaAbort' } // microphone permission denied / capture failed
  // armed timers firing
  | { t: 'timer'; which: TimerId };

// ---------------------------------------------------------------------------
// Effects
// ---------------------------------------------------------------------------

export type Effect =
  /** Open a peer connection; caller role creates the offer → `call.invite`. */
  | { e: 'openPeer'; callId: string; peer: string; role: CallRole }
  | { e: 'closePeer' }
  /** Answer the cached invite for `callId` (create answer → `call.accept`). */
  | { e: 'sendAccept'; callId: string }
  | { e: 'sendRinging'; callId: string }
  | { e: 'sendDecline'; callId: string; reason: CallDeclineReason }
  | { e: 'sendEnd'; callId: string; reason: CallEndReason }
  | { e: 'restartIce' }
  | { e: 'startTimer'; which: TimerId; ms: number }
  | { e: 'stopTimer'; which: TimerId }
  /** Publish the end of a call (UI toast / recents / ring stop). */
  | { e: 'callEnded'; callId: string; peer: string; direction: CallDirection; cause: CallEndCause };

export interface Transition {
  state: MachineState;
  effects: Effect[];
}

// ---------------------------------------------------------------------------
// The reducer
// ---------------------------------------------------------------------------

const same = (state: MachineState): Transition => ({ state, effects: [] });

function direction(call: MachineCall): CallDirection {
  return call.role === 'caller' ? 'outgoing' : 'incoming';
}

/** All timers that could be armed for the current phase, for teardown. */
const ALL_TIMERS: readonly TimerId[] = ['ring', 'connect', 'reconnect'];

function endCall(
  state: MachineState,
  cause: CallEndCause,
  opts: { sendReason?: CallEndReason; declineReason?: CallDeclineReason } = {},
): Transition {
  const call = state.call;
  if (!call) return same(state);
  const effects: Effect[] = ALL_TIMERS.map((which) => ({ e: 'stopTimer', which }));
  if (opts.sendReason) effects.push({ e: 'sendEnd', callId: call.callId, reason: opts.sendReason });
  if (opts.declineReason)
    effects.push({ e: 'sendDecline', callId: call.callId, reason: opts.declineReason });
  effects.push({ e: 'closePeer' });
  effects.push({
    e: 'callEnded',
    callId: call.callId,
    peer: call.peer,
    direction: direction(call),
    cause,
  });
  return {
    state: {
      ...state,
      phase: 'idle',
      call: null,
      shadowedInviteId: null,
      endedCallIds: rememberEnded(state.endedCallIds, call.callId),
    },
    effects,
  };
}

export function transition(selfUuid: string, state: MachineState, ev: MachineEvent): Transition {
  // --- events valid in any phase ------------------------------------------

  if (ev.t === 'remoteEnd' && state.shadowedInviteId === ev.callId) {
    // The higher-UUID peer cancelled its half of a crossed call — expected,
    // silent, does not touch our own outgoing call. (The id was already marked
    // terminal when we shadowed it; rememberEnded is idempotent.)
    return {
      state: {
        ...state,
        shadowedInviteId: null,
        endedCallIds: rememberEnded(state.endedCallIds, ev.callId),
      },
      effects: [],
    };
  }

  if (ev.t === 'invite' && state.endedCallIds.includes(ev.callId)) {
    // TERMINAL IS TERMINAL: a duplicate/late invite for a call we already
    // finished (declined, missed, hung up, crossed…) is dropped — never a
    // second ring, never a busy decline, never a resurrected call. Idempotent
    // dedupe of the SAME live invite is handled per-phase below.
    return same(state);
  }

  switch (state.phase) {
    case 'idle':
      return idle(state, ev);
    case 'dialing':
    case 'ringing_remote':
      return dialing(selfUuid, state, ev);
    case 'ringing':
      return ringing(state, ev);
    case 'connecting':
      return connecting(state, ev);
    case 'in_call':
      return inCall(state, ev);
    case 'reconnecting':
      return reconnecting(state, ev);
  }
}

// --- phase: idle ------------------------------------------------------------

function idle(state: MachineState, ev: MachineEvent): Transition {
  switch (ev.t) {
    case 'dial': {
      const call: MachineCall = { callId: ev.callId, peer: ev.peer, role: 'caller' };
      return {
        state: { ...state, phase: 'dialing', call },
        effects: [
          { e: 'openPeer', callId: ev.callId, peer: ev.peer, role: 'caller' },
          { e: 'startTimer', which: 'ring', ms: TIMEOUTS.RING_MS },
        ],
      };
    }
    case 'invite': {
      const call: MachineCall = { callId: ev.callId, peer: ev.from, role: 'callee' };
      return {
        state: { ...state, phase: 'ringing', call },
        effects: [
          { e: 'sendRinging', callId: ev.callId },
          { e: 'startTimer', which: 'ring', ms: ev.ringMs ?? TIMEOUTS.RING_MS },
        ],
      };
    }
    default:
      return same(state);
  }
}

// --- phase: dialing / ringing_remote (outgoing, unanswered) ------------------

function dialing(selfUuid: string, state: MachineState, ev: MachineEvent): Transition {
  const call = state.call!;
  switch (ev.t) {
    case 'remoteRinging':
      if (ev.callId !== call.callId) return same(state);
      return { state: { ...state, phase: 'ringing_remote' }, effects: [] };

    case 'remoteAccept':
      // Normal from ringing_remote; also accepted straight from dialing
      // (callee may answer before its `call.ringing` reaches us).
      if (ev.callId !== call.callId) return same(state);
      return {
        state: { ...state, phase: 'connecting' },
        effects: [
          { e: 'stopTimer', which: 'ring' },
          { e: 'startTimer', which: 'connect', ms: ICE_CONNECT_TIMEOUT_MS },
        ],
      };

    case 'invite': {
      if (ev.callId === call.callId) return same(state); // our own live id: dedupe, as every other phase does
      if (ev.from !== call.peer) {
        // Someone else while we're busy dialing: auto-decline busy.
        return {
          state,
          effects: [{ e: 'sendDecline', callId: ev.callId, reason: 'busy' }],
        };
      }
      // Crossed call (glare): both dialed each other. Never "busy".
      return crossedInvite(selfUuid, state, call, ev.callId);
    }

    case 'remoteDecline':
      if (ev.callId !== call.callId) return same(state);
      return endCall(state, ev.reason === 'busy' ? 'busy' : 'declined');

    case 'remoteEnd':
      if (ev.callId !== call.callId) return same(state);
      return endCall(state, mapRemoteEnd(ev.reason, call, state.phase));

    case 'hangup':
      // Caller cancels their own outgoing invite.
      return endCall(state, 'hangup', { sendReason: 'hangup' });

    case 'securityAbort':
      // e.g. a forged `call.accept` for our outgoing invite.
      return endCall(state, 'security', { sendReason: 'error' });

    case 'mediaAbort':
      // getUserMedia denied/failed — the user's own device, not the peer or
      // the wire; must not read as a security event in the UI.
      return endCall(state, 'mic_denied', { sendReason: 'error' });

    case 'timer':
      if (ev.which !== 'ring') return same(state);
      // 45 s, nobody answered. The server also expires the invite; we tear
      // down locally without sending anything.
      return endCall(state, 'no_answer');

    default:
      return same(state);
  }
}

/**
 * Crossed-call resolution (docs/02 §4): the HIGHER-UUID peer cancels its own
 * outgoing invite with `call.end {crossed}` and auto-accepts the incoming
 * one. The lower-UUID peer keeps dialing and shadows the incoming invite —
 * it will be cancelled by the peer, and must not ring or get "busy".
 */
function crossedInvite(
  selfUuid: string,
  state: MachineState,
  call: MachineCall,
  incomingCallId: string,
): Transition {
  if (isPolite(selfUuid, call.peer)) {
    // We are the higher UUID: cancel ours, auto-accept theirs.
    const newCall: MachineCall = { callId: incomingCallId, peer: call.peer, role: 'callee' };
    return {
      state: { ...state, phase: 'connecting', call: newCall },
      effects: [
        { e: 'stopTimer', which: 'ring' },
        { e: 'sendEnd', callId: call.callId, reason: 'crossed' },
        { e: 'closePeer' },
        { e: 'openPeer', callId: incomingCallId, peer: call.peer, role: 'callee' },
        { e: 'sendAccept', callId: incomingCallId },
        { e: 'startTimer', which: 'connect', ms: ICE_CONNECT_TIMEOUT_MS },
      ],
    };
  }
  // We are the lower UUID: hold course; shadow their invite. The shadowed id is
  // TERMINAL FROM THIS MOMENT, not from the peer's cancel: that `call.end
  // {crossed}` can be lost, or be reordered ahead of the invite it cancels, and
  // a replay of the losing invite must never ring — mid-call it would otherwise
  // supersede the very call this tiebreak just won.
  return {
    state: {
      ...state,
      shadowedInviteId: incomingCallId,
      endedCallIds: rememberEnded(state.endedCallIds, incomingCallId),
    },
    effects: [],
  };
}

/**
 * A VERIFIED invite from the peer of our CURRENT call under a NEW callId (the
 * engine checks the signature before this event exists). A peer's machine can
 * only dial from idle, so this is proof their side of our call is already
 * dead — their hangup was lost, or their app restarted. It also proves the
 * server no longer tracks our call: otherwise its busy rule would have
 * auto-declined this invite before relaying it. Busy-declining it ourselves
 * deadlocked the phones — one wedged in "Connecting…", the other hearing
 * "Busy" on every redial (field 2026-07-18). Instead: end the stale call and
 * ring the fresh one.
 */
function supersedeByPeerInvite(
  state: MachineState,
  cause: CallEndCause,
  sendReason: CallEndReason | undefined,
  ev: Extract<MachineEvent, { t: 'invite' }>,
): Transition {
  const ended = endCall(state, cause, sendReason ? { sendReason } : {});
  const rering = idle(ended.state, ev);
  return { state: rering.state, effects: [...ended.effects, ...rering.effects] };
}

// --- phase: ringing (incoming) ------------------------------------------------

function ringing(state: MachineState, ev: MachineEvent): Transition {
  const call = state.call!;
  switch (ev.t) {
    case 'accept':
      return {
        state: { ...state, phase: 'connecting' },
        effects: [
          { e: 'stopTimer', which: 'ring' },
          { e: 'openPeer', callId: call.callId, peer: call.peer, role: 'callee' },
          { e: 'sendAccept', callId: call.callId },
          { e: 'startTimer', which: 'connect', ms: ICE_CONNECT_TIMEOUT_MS },
        ],
      };

    case 'decline':
    case 'hangup':
      // Decline, or a generic end/hang-up control on the incoming screen (and
      // engine.stop() during a ring): rejecting the ring is a decline. Without
      // this a `hangup` here was dropped, stranding the callee ringing.
      return endCall(state, 'declined', { declineReason: 'declined' });

    case 'invite':
      if (ev.callId === call.callId) return same(state); // duplicate delivery
      // Same peer re-dialing = our ring is stale (their first dial died);
      // nothing to send for the old ring — the server provably dropped it.
      if (ev.from === call.peer) return supersedeByPeerInvite(state, 'missed', undefined, ev);
      return {
        state,
        effects: [{ e: 'sendDecline', callId: ev.callId, reason: 'busy' }],
      };

    case 'remoteEnd':
      if (ev.callId !== call.callId) return same(state);
      // hangup = caller gave up (missed); answered_elsewhere = our other
      // session took it (silent stop, no missed badge).
      return endCall(state, mapRemoteEnd(ev.reason, call, state.phase));

    case 'securityAbort':
    case 'mediaAbort':
      // A local failure while the phone is ringing (a late-caught forged
      // invite, a mic that will not open): reject the ring NOW. A ring is not
      // a connection the prime directive protects, and 45 s of phantom ringing
      // after the engine already gave up is worse than an honest decline —
      // which also stops the ring on our other sessions.
      return endCall(state, ev.t === 'securityAbort' ? 'security' : 'mic_denied', {
        declineReason: 'declined',
      });

    case 'timer':
      if (ev.which !== 'ring') return same(state);
      return endCall(state, 'missed');

    default:
      return same(state);
  }
}

// --- phase: connecting ---------------------------------------------------------

function connecting(state: MachineState, ev: MachineEvent): Transition {
  const call = state.call!;
  switch (ev.t) {
    case 'iceConnected':
      return {
        state: { ...state, phase: 'in_call' },
        effects: [{ e: 'stopTimer', which: 'connect' }],
      };

    case 'iceFailed':
    case 'networkChange':
      // A HARD ICE failure during setup (all candidate pairs exhausted —
      // e.g. the ICE-server fetch came up empty on a doze-woken phone):
      // waiting out the 20 s timer is pure dead air. Restart immediately;
      // the engine re-fetches ICE servers first when it had none, and the
      // connect timer still caps the whole attempt (field 2026-07-19).
      // A network change (Wi-Fi ↔ LTE) mid-setup is the same bet: the
      // candidates we are gathering belong to an interface that just went
      // away, so restart onto the new one rather than burn the 20 s budget
      // negotiating a dead path — exactly what in_call/reconnecting do.
      return { state, effects: [{ e: 'restartIce' }] };

    case 'timer':
      if (ev.which !== 'connect') return same(state);
      // 20 s without ICE ⇒ failed (docs/02 §4).
      return endCall(state, 'failed', { sendReason: 'error' });

    case 'hangup':
      return endCall(state, 'hangup', { sendReason: 'hangup' });

    case 'remoteEnd':
      if (ev.callId !== call.callId) return same(state);
      return endCall(state, mapRemoteEnd(ev.reason, call, state.phase));

    case 'securityAbort':
      return endCall(state, 'security', { sendReason: 'error' });

    case 'mediaAbort':
      return endCall(state, 'mic_denied', { sendReason: 'error' });

    case 'invite':
      if (ev.callId === call.callId) return same(state);
      // Our peer re-dialing while we "connect" to them = this setup is for a
      // call their side already abandoned (the field wedge): ring them.
      if (ev.from === call.peer) return supersedeByPeerInvite(state, 'failed', 'hangup', ev);
      return { state, effects: [{ e: 'sendDecline', callId: ev.callId, reason: 'busy' }] };

    default:
      // iceDisconnected during setup is normal churn; the connect timer is
      // the arbiter. Never tear down early — Connection > everything.
      return same(state);
  }
}

// --- phase: in_call -------------------------------------------------------------

function inCall(state: MachineState, ev: MachineEvent): Transition {
  const call = state.call!;
  switch (ev.t) {
    case 'iceDisconnected':
    case 'iceFailed':
    case 'rtpStalled':
      // Degrade to Reconnecting, never drop: restart ICE, arm the 60 s budget.
      // `iceFailed` lands here too: today's engine reports a hard failure on a
      // live call as `iceDisconnected`, but a host that reports it verbatim
      // must not leave the call frozen with no budget armed.
      return {
        state: { ...state, phase: 'reconnecting' },
        effects: [
          { e: 'restartIce' },
          { e: 'startTimer', which: 'reconnect', ms: TIMEOUTS.RECONNECT_BUDGET_MS },
        ],
      };

    case 'networkChange':
      // Proactive restart onto the new network; media may not even blip, so
      // stay in_call — the banner appears only if ICE actually drops.
      return { state, effects: [{ e: 'restartIce' }] };

    case 'hangup':
      return endCall(state, 'hangup', { sendReason: 'hangup' });

    case 'remoteEnd':
      if (ev.callId !== call.callId) return same(state);
      return endCall(state, mapRemoteEnd(ev.reason, call, state.phase));

    case 'rtpTimeout':
      // 30 s without media despite a restart attempt (docs/02 §4).
      return endCall(state, 'connection_lost', { sendReason: 'timeout' });

    case 'securityAbort':
      return endCall(state, 'security', { sendReason: 'error' });

    case 'mediaAbort':
      return endCall(state, 'mic_denied', { sendReason: 'error' });

    case 'invite':
      if (ev.callId === call.callId) return same(state);
      // Peer re-dialing mid-"call": their side is gone (app restart / lost
      // hangup) and our media is a zombie — take the new call.
      if (ev.from === call.peer) return supersedeByPeerInvite(state, 'hangup', 'hangup', ev);
      return { state, effects: [{ e: 'sendDecline', callId: ev.callId, reason: 'busy' }] };

    default:
      return same(state);
  }
}

// --- phase: reconnecting ---------------------------------------------------------

function reconnecting(state: MachineState, ev: MachineEvent): Transition {
  const call = state.call!;
  switch (ev.t) {
    case 'iceConnected':
      return {
        state: { ...state, phase: 'in_call' },
        effects: [{ e: 'stopTimer', which: 'reconnect' }],
      };

    case 'iceFailed':
    case 'networkChange':
      // Retry within the budget (session applies backoff between attempts).
      return { state, effects: [{ e: 'restartIce' }] };

    case 'timer':
      if (ev.which !== 'reconnect') return same(state);
      // 60 s without recovery — concede the call.
      return endCall(state, 'connection_lost', { sendReason: 'timeout' });

    case 'rtpTimeout':
      return endCall(state, 'connection_lost', { sendReason: 'timeout' });

    case 'hangup':
      return endCall(state, 'hangup', { sendReason: 'hangup' });

    case 'remoteEnd':
      if (ev.callId !== call.callId) return same(state);
      return endCall(state, mapRemoteEnd(ev.reason, call, state.phase));

    case 'securityAbort':
      return endCall(state, 'security', { sendReason: 'error' });

    case 'mediaAbort':
      return endCall(state, 'mic_denied', { sendReason: 'error' });

    case 'invite':
      if (ev.callId === call.callId) return same(state);
      if (ev.from === call.peer) return supersedeByPeerInvite(state, 'hangup', 'hangup', ev);
      return { state, effects: [{ e: 'sendDecline', callId: ev.callId, reason: 'busy' }] };

    default:
      return same(state);
  }
}

// ---------------------------------------------------------------------------

function mapRemoteEnd(reason: CallEndReason, call: MachineCall, phase: CallPhase): CallEndCause {
  switch (reason) {
    case 'hangup':
      // Caller hanging up while we are still RINGING = a missed call for us;
      // in any established phase a remote hangup is just a hangup.
      return call.role === 'callee' && phase === 'ringing' ? 'missed' : 'hangup';
    case 'timeout':
      // The server expires an unanswered invite at 45 s and fans call.end
      // {timeout} to BOTH sides (docs/02 §4) — and its timer, armed when the
      // invite arrived, beats our own ring timer. Rendering that as "connection
      // lost" mislabels the commonest outcome there is: the callee's missed
      // call (which then never reaches the missed badge, since a never-
      // connected connection_lost logs as `failed`), and the caller's plain
      // no-answer. Only an ESTABLISHED call lost to a timeout is a lost
      // connection.
      if (call.role === 'callee' && phase === 'ringing') return 'missed';
      if (call.role === 'caller' && (phase === 'dialing' || phase === 'ringing_remote'))
        return 'no_answer';
      return 'connection_lost';
    case 'answered_elsewhere':
      return 'answered_elsewhere';
    case 'crossed':
      // A crossed cancel for OUR call id would be a peer bug; treat as hangup.
      return 'hangup';
    case 'error':
      return 'failed';
  }
}

// ---------------------------------------------------------------------------
// Stateful wrapper
// ---------------------------------------------------------------------------

export class CallMachine {
  private stateValue: MachineState = IDLE_STATE;

  constructor(private readonly selfUuid: string) {}

  get state(): MachineState {
    return this.stateValue;
  }

  /** Apply an event; returns the effects for the runtime to execute. */
  dispatch(ev: MachineEvent): Effect[] {
    const { state, effects } = transition(this.selfUuid, this.stateValue, ev);
    this.stateValue = state;
    return effects;
  }

  reset(): void {
    this.stateValue = IDLE_STATE;
  }
}
